<template>
  <main class="page">
    <section class="page__intro">
        <h1 class="page__title">Din sammanställning</h1>
        <p>Med enkla medel kan du öka din pension. Ifall låter oss hjälpa dig med din totala ekonomi kommande år och genomför de kontinuerliga förändringar som vi håller koll på åt dig. Då kommer du enkelt kunna påverka din framtida pension.</p>
    </section>
    <LoadingSpinner v-if="isLoading"/>
    <section class="page__main" v-else>
      <Cards :cards="cards" />
    </section>
  </main>
</template>

<script>
import { LoadingSpinner } from '../components';
import { Cards, RegularCard, TableCard } from "@/components/overview";
import Jumbotron from '../components/header/Jumbotron';
import { mapActions, mapGetters } from 'vuex';
import chartLine from '../assets/svg/chart-line-duotone.svg';

export default {
  name: 'Overview',
  data() {
    return {
      showResponse: false,
      response: '',
      shownAge: '',
    }
  },
  components: { Cards, Jumbotron, LoadingSpinner },
  created () {
    if(!this.insurances.status) { // Happens only once
      this.getInsurances()
    }

    this.getFinancialSummary()
  },
  methods: {
    ...mapActions([ 'getInsurances', 'getFinancialSummary' ]),
    numberSpacing (nr) {
      return nr.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")
    },
    checkIfNull (value) {
      if(value == null) {
        return 'X'
      } else {
        return this.numberSpacing(value)
      }
    },
    checkInsurancesTrafficLight() {
      let insurancesTrafficLight = 3 // Red as standard.

      let hasSjukforsakring = this.insurances.ins.overview.hasSjukforsakring
      let hasEfterlevnadsskydd = this.insurances.ins.overview.hasEfterlevnadsskydd

      if(hasSjukforsakring && hasEfterlevnadsskydd) {
        insurancesTrafficLight = 1
      } else if (hasSjukforsakring || hasEfterlevnadsskydd) {
        insurancesTrafficLight = 2
      }

      return insurancesTrafficLight
    }
  },
  computed: {
    ...mapGetters(['user', 'insurances', 'financialSummary']),
    isLoading: function () {
      return this.insurances.status === 'loading'
    },
    cards: function () {
      let insurances = this.$store.getters.insurances
      let financialSummary = this.$store.getters.financialSummary.summary // For table

      return (
        [
          {
            component: RegularCard,
            options: {
              type: 'horizontal', // regular, horizontal, vertical
              dataType: 'multiple', // regular, multiple, table
              title: 'Min pension',
              items: {
                0: {
                  subtitle: 'Förväntad pension vid ' + this.checkIfNull(insurances.calculations?.goal?.pensionAge) + ' år.',
                  valueType: 'monthly', // percent, monthly, sek
                  value: this.checkIfNull(insurances.calculations?.nettoPensionMonthly),
                  imageURL: '',
                  info: true
                },
                1: {
                  subtitle: 'Samma pension med enkla förändringar',
                  valueType: 'monthly', // percent, monthly, sek
                  value: this.checkIfNull(insurances.calculations?.nettoPensionMonthlyFuture),
                  imageURL: '',
                  info: true
                },
              }
            }
          },
          /*{
            component: RegularCard,
            options: {
              type: 'regular',
              dataType: 'regular',
              title: 'Förändring med POND',
              trafficLightStatus: 1,
              items: {
                0: {
                  subtitle: 'Förändring sedan start',
                  valueType: 'percent',
                  value: '+12', // Behövs backend
                  //imageURL: chartLine,
                  info: false
                }
              }
            }
          },*/
          {
            component: RegularCard,
            options: {
              type: 'regular',
              dataType: 'regular',
              title: 'Allmän pension',
              trafficLightStatus: insurances.pensions.overview.totalAmount > 550000 ? 1 : 2,
              items: {
                0: {
                  subtitle: 'Mitt saldo',
                  valueType: 'sek',
                  value: this.numberSpacing(insurances.pensions.overview.totalAmount),
                  imageURL: '',
                  info: false
                }
              }
            }
          },
          {
            component: TableCard,
            options: {
              type: 'vertical',
              dataType: 'regular',
              title: 'Test',
              total: this.checkIfNull(financialSummary.totalSum),
              items: {
                1: {
                  name: 'Pensionskapital',
                  value: this.checkIfNull(financialSummary.totalPension) + ' kr',
                  link: false
                },
                2: {
                  name: 'Lönekonto',
                  value: this.checkIfNull(financialSummary.account),
                  link: true
                },
                3: {
                  name: 'Sparkonton / ISK',
                  value: this.checkIfNull(financialSummary.savingsAccount),
                  link: true
                },
                4: {
                  name: 'Fastigheter',
                  value: this.checkIfNull(financialSummary.realEstate),
                  link: true
                },
                5: {
                  name: 'Fordon',
                  value: this.checkIfNull(financialSummary.vehicle),
                  link: true
                },
                6: {
                  name: 'Båt',
                  value: this.checkIfNull(financialSummary.boat),
                  link: true
                },
                7: {
                  name: 'Övrigt',
                  value: this.checkIfNull(financialSummary.other),
                  link: true
                },
                8: {
                  name: 'Lån',
                  value: this.checkIfNull(financialSummary.loan),
                  link: true
                },
              }
            }
          },
          {
            component: RegularCard,
            options: {
              type: 'regular',
              dataType: 'multiple',
              title: 'Tjänstepension',
              trafficLightStatus: insurances.ins.overview.tjanstepension != 0 ? 2 : 3,
              items: {
                0: {
                  subtitle: 'Mitt saldo',
                  valueType: 'sek',
                  value: this.numberSpacing(insurances.ins.overview.tjanstepension),
                  imageURL: '',
                  info: false
                },
                1: {
                  subtitle: 'Min premie',
                  valueType: 'sek',
                  value: 1000, // Behövs backend
                  imageURL: '',
                  info: false
                }
              }
            }
          },
          {
            component: RegularCard,
            options: {
              type: 'regular',
              dataType: 'regular',
              title: 'Totalt pensionskapital',
              trafficLightStatus: 2,
              items: {
                0: {
                  subtitle: 'Mitt saldo',
                  valueType: 'sek',
                  value: this.checkIfNull(insurances.ins.overview.totalAmount + insurances.pensions.overview.totalAmount),
                  imageURL: '',
                  info: false
                }
              }
            }
          },
          {
            component: RegularCard,
            options: {
              type: 'regular',
              dataType: 'list',
              title: 'Trygghetsförsäkringar',
              trafficLightStatus: this.checkInsurancesTrafficLight(),
              url: '/insurances',
              items: {
                0: {
                  info: false,
                  listItems: {
                    hasSjukforsakring: insurances.ins.overview.hasSjukforsakring,
                    hasEfterlevnadsskydd: insurances.ins.overview.hasEfterlevnadsskydd,
                  }
                }
              }
            }
          },
        ]
      )
    }
  },
}
</script>
